'use client';

import React, { useState } from 'react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { motion } from 'framer-motion';

const ConcreteCracking = () => {
  const [heroSrc, setHeroSrc] = useState('/images/blogs/concretecracking/5.webp');

  return (
    <>
      <Navbar />

      {/* Hero */}
      <section className="relative h-[60vh] flex items-center justify-center overflow-hidden">
        <img
          src={heroSrc}
          alt="Blog Hero"
          onError={() => setHeroSrc('/images/blogs/concretecracking/thumb.webp')}
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-black/45" />
        <motion.div
          className="relative z-10 text-center text-white px-6"
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1 }}
        >
          <h1 className="text-4xl md:text-6xl font-light tracking-wide mb-4">
            Undoing through the cracking concrete
          </h1>
          
        </motion.div>
      </section>

      {/* Blog Content */}
      <section className="bg-white py-24">
        <div className="max-w-4xl mx-auto px-6 space-y-20">

          {/* Intro */}
          <div className="space-y-6 text-lg leading-relaxed text-neutral-700">
            <p>
              The year is 2040. Location: Flatiron district, Manhattan, New York City. We have surpassed the 1.5 degree average temperature rise benchmark. Carbon neutral is not enough anymore, we must be carbon negative. Old infrastructures have begun to crumble and fail, but we cannot demolish them, the cost is too high. With the unprecedented demand for more construction and housing, we must use everything, even the cracking concrete. It is from within these cracks that we must find the opportunity to restitch the urban fabric.
            </p>
            <p>
             Concrete isn’t going anywhere. The standardization of chemical and physical composition for predictable mass production forced us into an overdependence on an invariable material: Portland Cement Concrete. We scour and burn limestone from every corner of the earth, often far from the site of construction, and slap concrete onto every surface. Geologies and mineral compositions of the soil and their roles in ecosystems differ all across the globe. The performative roles of the building envelope vary from climate to climate. The historical, cultural, social and labor associations people have with their building materials changes from region to region. The standardization and unprejudiced use of Portland Cement concrete goes against the ecological, cultural, climactic progression and integration of humankind. We need to posit a material strategy for the structural, ecological and social diversification of concrete.
            </p>
          </div>

          {/* Image 1 */}
          <figure className="space-y-3">
            <img
              src={`${process.env.PUBLIC_URL}/images/blogs/concretecracking/5.webp`}
              alt="Material detail"
              className="w-full h-[500px] object-cover rounded-2xl"
            />
            <figcaption className="text-sm text-neutral-500">
              Fig 1. Subtle variation in stone texture responding to light.
            </figcaption>
          </figure>

          {/* Section */}
          <div className="space-y-6 text-lg leading-relaxed text-neutral-700">
            <h2 className="text-2xl font-medium text-neutral-900">Material as Narrative</h2>
            <p>
              Manhattan's profit grabbing real estate frenzy fetishes and takes pride in the concrete jungle it has developed. The city that yells “I’m walking over here!” and closes streets and traffic for urban re-engagement, gladly pours concrete on soil for pavements. We have closed up the earthen pores of the city, creating a watertight concrete barrier beneath us, burying old existing rivers and marshlands now concretized as impermeable drainage systems. Now that the ocean pushes back, the sea water rises from under the concrete barrier and wreaks havoc on the foundations of the city of skyscrapers.
            </p>
          </div>

          {/* Image 2 + 3 Grid */}
          <div className="grid md:grid-cols-2 gap-8">
            <img
              src={`${process.env.PUBLIC_URL}/images/blogs/concretecracking/1.webp`}
              alt="Interior light"
              className="w-full h-[350px] object-cover rounded-2xl"
            />
            <img
              src={`${process.env.PUBLIC_URL}/images/blogs/concretecracking/2.webp`}
              alt="Shadow study"
              className="w-full h-[350px] object-cover rounded-2xl"
            />
          </div>

          {/* Pull Quote */}
          <blockquote className="border-l-4 border-neutral-300 pl-8 italic text-xl text-neutral-600">
            “Good architecture does not shout. It listens, frames, and holds.”
          </blockquote>

          {/* Image 4 */}
          <figure className="space-y-3">
            <img
              src={`${process.env.PUBLIC_URL}/images/blogs/concretecracking/3.webp`}
              alt="Courtyard light"
              className="w-full h-[500px] object-cover rounded-2xl"
            />
            <figcaption className="text-sm text-neutral-500">
              Fig 2. Courtyard acting as light regulator and thermal buffer.
            </figcaption>
          </figure>

          {/* Section */}
          <div className="space-y-6 text-lg leading-relaxed text-neutral-700">
            <h2 className="text-2xl font-medium text-neutral-900">Material as Narrative</h2>
            <p>
              An icon of the past, the Flatiron building in Midtown New York has been the latest victim of this undermining. Its three subterranean levels share a wall with the R and W subway station which have been facing recurring inundation. Originally, the swamplands of Madison Square park were drained by Minetta Creek and Cedar Creek which were converted into covered drains. Now these drains become the cracks through which seawater pushes in and alters the salinity and acidity of the ground. These pressures have finally broken through into the old boiler rooms of the Flatiron building, and the building has begun to fall apart. The building has already been in constant repair for dozens of years now because of its failing limestone blocks and cracking facade. There has been scaffolding for so long on the building that now the scaffolding has become a part of the identity of the building. For years the struggle lay in trying to restore the building back to its older glory, but I ask why? The identity of the city and the neighborhood has changed, our architectural abilities have changed, why shouldn't we take this opportunity to fill in the cracks to undo pasts.</p>
          </div>

          {/* Image 5 + 6 Grid */}
          <div className="grid md:grid-cols-2 gap-8">
            <img
              src={`${process.env.PUBLIC_URL}/images/blogs/concretecracking/4.webp`}
              alt="Sectional study"
              className="w-full h-[350px] object-cover rounded-2xl"
            />
            <img
              src={`${process.env.PUBLIC_URL}/images/blogs/concretecracking/6.webp`}
              alt="Facade rhythm"
              className="w-full h-[350px] object-cover rounded-2xl"
            />
          </div>
          {/* Section */}
          <div className="space-y-6 text-lg leading-relaxed text-neutral-700">
            <h2 className="text-2xl font-medium text-neutral-900">Material as Narrative</h2>
            <p>
              The scaffolding for me presents an alternative strategy of developing a culture of constant repair that keeps up with the fast changing identities of New York City. I begin by re-engaging members of society to contribute to the design to decentralize the aesthetic agency to the public realm who are directly engaging and identifying with historic icons such as the Flatiron building. 
              </p>
          </div>
          <p>
              Currently I am working with Mia, a community botanical engineer, Malik, a youth empowerment coordinator, and Mei, a cultural heritage preservationist. With them I am developing prototypes of concrete alternatives that each perform different functions along the lines of porosity, biological habitability, carbon sequestration, creative expression, structural abilities, and so on. By using these alternatives in conjunction with each other we are able to create structures that perform and respond dynamically and un-isotropically. </p>

          {/* Image 5 + 6 Grid */}
          <div className="grid md:grid-cols-2 gap-8">
            <img
              src={`${process.env.PUBLIC_URL}/images/blogs/concretecracking/7.webp`}
              alt="Sectional study"
              className="w-full h-[350px] object-cover rounded-2xl"
            />
            <img
              src={`${process.env.PUBLIC_URL}/images/blogs/concretecracking/8.webp`}
              alt="Facade rhythm"
              className="w-full h-[350px] object-cover rounded-2xl"
            />
          </div>

          {/* Section */}
          <div className="space-y-6 text-lg leading-relaxed text-neutral-700">
            <h2 className="text-2xl font-medium text-neutral-900">Material as Narrative</h2>
            <p>
              The cementitious base of the concrete has been switched from portland cement to Magnesium Oxide based cements, a technology developed by Columbia University in the mid 2020’s. Magnesium Oxide, derived straight from electrolysis of seawater, is a highly versatile material that can be made cementitious by a variety of methods using phosphates, sulfates, and even through simple hydration, and continue to sequester carbon throughout its life. Its versatility directly plugs it into several variable local ecosystems which disincentivizes the need for distant resources.</p>
          </div>
          <p>
              By adding pigments to the different prototypes of concrete, their conjunctional use becomes akin to painting where following expressive directives, structural analysis or biological gradients leads to an architecture that responds dynamically on a spectrum of parameters. A wall becomes not just a physical barrier but also a host for ecology and human identity.</p>
              <p>
              Re-enforcing the cracks in the flatiron building presents to us an opportunity to recast its identity and role not only as a cultural representative but as a physical part of our ecologically urban fabric. This technique of filling in the cracks with a variety of concrete addresses infrastructural injustices that might exist by not entirely undermining them but slowly transforming them through their failures. Our first experiment: undoing the ornaments of the cracking column of the flatiron building.
</p>


          {/* Author */}
          <div className="flex items-center gap-6 pt-12 border-t">
            <img
              src={`${process.env.PUBLIC_URL}/images/team/aditya-mehta.png`}
              alt="Author"
              className="w-20 h-20 object-cover rounded-full"
            />
            <div>
              <p className="font-medium text-neutral-900">Aditya Mehta</p>
              <p className="text-sm text-neutral-500">Project Architect, Studio Sharan</p>
            </div>
          </div>

          

        </div>
      </section>

      <Footer />
    </>
  );
};

export default ConcreteCracking;
